// Per-business localStorage cache of the autofill payload returned by
// GET /api/estimates/recent-context. Two reasons it lives client-side as well
// as on the server:
//
// 1. Synchronous initial render. The new-estimate form auto-seeds the first
//    line on mount; without a synchronous source the seeded line bakes in the
//    catalog default before the server query resolves, then never refreshes.
//    Reading from localStorage in useState's lazy initializer means lastRates
//    is available the instant the component renders.
//
// 2. Offline / slow network resilience. Item-name suggestions and the gold
//    rate the user typed yesterday are useful even if the device is on a
//    flaky 2G connection or temporarily offline. The server fetch then
//    reconciles in the background and overwrites the cache with the
//    authoritative business-wide pool.
//
// Cross-business isolation: keyed by businessId so switching businesses (e.g.
// platform_owner debugging) cannot bleed one shop's rates into another.

const KEY_PREFIX = 'jcalc.recentContext.';
// Max entries we will persist client-side. Bounds localStorage footprint —
// 50 names + 50 rates is well under the 5MB budget per origin.
const MAX_NAMES = 50;
const MAX_RATES = 200;

export type RecentContextCache = {
  itemNames: string[];
  lastRates: Record<string, number>;
  updatedAt: number;
};

function key(businessId: string): string {
  return `${KEY_PREFIX}${businessId}`;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isRateMap(v: unknown): v is Record<string, number> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  for (const value of Object.values(v as Record<string, unknown>)) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return false;
  }
  return true;
}

export function getCachedRecentContext(businessId: string): RecentContextCache | null {
  if (!businessId) return null;
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key(businessId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    const itemNames = isStringArray(obj['itemNames']) ? obj['itemNames'] : [];
    const lastRates = isRateMap(obj['lastRates']) ? obj['lastRates'] : {};
    const updatedAt = typeof obj['updatedAt'] === 'number' ? obj['updatedAt'] : 0;
    return { itemNames, lastRates, updatedAt };
  } catch {
    return null;
  }
}

export function setCachedRecentContext(
  businessId: string,
  ctx: { itemNames: ReadonlyArray<string>; lastRates: Readonly<Record<string, number>> },
): void {
  if (!businessId) return;
  if (typeof localStorage === 'undefined') return;
  // Trim before persist so a runaway server response can't blow the quota.
  const trimmedNames = ctx.itemNames.slice(0, MAX_NAMES);
  const trimmedRates: Record<string, number> = {};
  let count = 0;
  for (const [k, v] of Object.entries(ctx.lastRates)) {
    if (count >= MAX_RATES) break;
    trimmedRates[k] = v;
    count++;
  }
  const payload: RecentContextCache = {
    itemNames: trimmedNames,
    lastRates: trimmedRates,
    updatedAt: Date.now(),
  };
  try {
    localStorage.setItem(key(businessId), JSON.stringify(payload));
  } catch {
    // Quota exceeded / private mode — silent. Cache is a nicety, not a contract.
  }
}

// Optimistic local update applied right after a successful save, so the next
// render picks up the just-typed rate without waiting for the server roundtrip
// + react-query invalidation. Server-side refetch will reconcile the same data
// (and any concurrent saves from other staff in the same business) shortly.
export function mergeIntoRecentContext(
  businessId: string,
  patch: { itemNames: ReadonlyArray<string>; lastRates: Readonly<Record<string, number>> },
): void {
  if (!businessId) return;
  const existing = getCachedRecentContext(businessId);
  // Names: most-recent-first dedupe, case-insensitive.
  const seen = new Set<string>();
  const itemNames: string[] = [];
  for (const name of [...patch.itemNames, ...(existing?.itemNames ?? [])]) {
    const trimmed = name.trim();
    if (trimmed.length === 0) continue;
    const k = trimmed.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    itemNames.push(trimmed);
    if (itemNames.length >= MAX_NAMES) break;
  }
  // Rates: patch wins over existing for the same key (just-typed is most recent).
  const lastRates: Record<string, number> = { ...(existing?.lastRates ?? {}), ...patch.lastRates };
  setCachedRecentContext(businessId, { itemNames, lastRates });
}
